// src/pages/TvSchedule.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import QRCode from "react-qr-code";
import NoSleep from "nosleep.js";
import "../styles/TvSchedule.css";
import logo from "../assets/logo.png";

const getFullName = (spec) => {
  if (!spec) return "";
  if (spec.specialistName) return spec.specialistName.trim();
  const surname = spec.surname || "";
  const firstName = spec.first_name || "";
  const patronymic = spec.patronymic || "";
  return `${surname} ${firstName} ${patronymic}`.trim();
};
const getSpecialistId = (spec) => spec?.specialistId ?? spec?.id ?? spec?.employeeId;


const russianMonths = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июль",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

const russianMonthsGen = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

const russianWeekdays = [
  "Воскресенье",
  "Понедельник",
  "Вторник",
  "Среда",
  "Четверг",
  "Пятница",
  "Суббота",
];

const scheduleDayKeys = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
];

const API_SCHEDULE_SPECIALISTS = "https://api3v.fenixkst.kz/api/schedule/v1/public/specialists";
const API_SCHEDULE_OCCURRENCES = "https://api3v.fenixkst.kz/api/schedule/v1/public/occurrences";
const API_QR_TODAY = "https://api3v.fenixkst.kz/qr-attendance/qr/public/today";

const scheduleToken = process.env.REACT_APP_PUBLIC_SCHEDULE_TOKEN;

const scheduleHeaders = {
  Authorization: `Bearer ${scheduleToken}`,
};

function getTimeHHMM(value) {
  if (!value) return "00:00";
  const str = String(value);
  if (str.includes("T")) return str.slice(11, 16);
  return str.slice(0, 5);
}

function getChildDisplayName(childName) {
  const parts = String(childName || "занято").trim().split(/\s+/).filter(Boolean);
  return parts[1] || parts[0] || "занято";
}

function normalizeName(value) {
  return String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
}

function isOccurrenceForSpecialist(occurrence, specialistId, specialistName) {
  if (!specialistName) return false;
  return normalizeName(occurrence?.specialistName) === normalizeName(specialistName);
}

function isVisibleOccurrence(occurrence) {
  if (occurrence?.state && occurrence.state !== "ACTIVE") return false;
  if (occurrence?.status === "CANCELLED") return false;
  return occurrence?.status === "DONE" || occurrence?.status === "PLANNED";
}

function mapOccurrenceToVisit(occurrence) {
  const dateYmd = String(occurrence.date || "").slice(0, 10);
  const timeHHMM = getTimeHHMM(occurrence.timeStart);

  return {
    ...occurrence,
    visitDates: [dateYmd],
    visitDateFirst: dateYmd,
    visitTime: `${timeHHMM}:00`,
    childDisplayName: getChildDisplayName(occurrence.childName),
    additional: occurrence.mark === "дополнительное занятие",
  };
}

async function fetchSpecialists() {
  const res = await fetch(API_SCHEDULE_SPECIALISTS, {
    headers: scheduleHeaders,
  });

  if (!res.ok) {
    throw new Error(`Failed to load schedule specialists: ${res.status}`);
  }

  return await res.json();
}

async function fetchVisits({ dateFrom, dateTo, specialistId, specialistName }) {
  const url = new URL(API_SCHEDULE_OCCURRENCES);
  url.searchParams.set("dateFrom", dateFrom);
  url.searchParams.set("dateTo", dateTo);
  const res = await fetch(url.toString(), {
    headers: scheduleHeaders,
  });

  if (!res.ok) {
    throw new Error(`Failed to load schedule occurrences: ${res.status}`);
  }

  const data = await res.json();
  const occurrences = Array.isArray(data) ? data : data.items || [];

  return occurrences
    .filter((occurrence) =>
      isOccurrenceForSpecialist(occurrence, specialistId, specialistName)
    )
    .filter(isVisibleOccurrence)
    .map(mapOccurrenceToVisit);
}

/* ========================= Wake Lock + NoSleep ========================= */

function useTvNoSleep(enabled = true) {
  const wakeLockRef = useRef(null);
  const noSleepRef = useRef(null);
  const enabledRef = useRef(false);

  const requestNativeWakeLock = async () => {
    if (!enabled) return;

    if (!("wakeLock" in navigator)) {
      console.warn("[TV NO SLEEP] Native Wake Lock не поддерживается");
      return;
    }

    if (document.visibilityState !== "visible") return;

    try {
      if (wakeLockRef.current) return;

      wakeLockRef.current = await navigator.wakeLock.request("screen");

      wakeLockRef.current.addEventListener("release", () => {
        console.warn("[TV NO SLEEP] Native Wake Lock был сброшен");
        wakeLockRef.current = null;
      });

      console.log("[TV NO SLEEP] Native Wake Lock включен");
    } catch (err) {
      console.warn("[TV NO SLEEP] Native Wake Lock не включился:", err);
    }
  };

  const enableNoSleepFallback = async () => {
    if (!enabled) return;

    try {
      if (!noSleepRef.current) {
        noSleepRef.current = new NoSleep();
      }

      if (enabledRef.current) return;

      await noSleepRef.current.enable();
      enabledRef.current = true;

      console.log("[TV NO SLEEP] NoSleep fallback включен");
    } catch (err) {
      console.warn("[TV NO SLEEP] NoSleep fallback не включился:", err);
    }
  };

  const enableAll = async () => {
    await requestNativeWakeLock();
    await enableNoSleepFallback();
  };

  const disableAll = async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch {}

    try {
      if (noSleepRef.current && enabledRef.current) {
        noSleepRef.current.disable();
        enabledRef.current = false;
      }
    } catch {}
  };

  useEffect(() => {
    enableAll();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        enableAll();
      }
    };

    const handleUserAction = () => {
      enableAll();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    window.addEventListener("focus", enableAll);
    window.addEventListener("click", handleUserAction);
    window.addEventListener("touchstart", handleUserAction);
    window.addEventListener("keydown", handleUserAction);
    window.addEventListener("mousemove", handleUserAction);

    const interval = setInterval(() => {
      enableAll();
    }, 30000);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      window.removeEventListener("focus", enableAll);
      window.removeEventListener("click", handleUserAction);
      window.removeEventListener("touchstart", handleUserAction);
      window.removeEventListener("keydown", handleUserAction);
      window.removeEventListener("mousemove", handleUserAction);

      clearInterval(interval);
      disableAll();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}

/* ========================= QR helpers ========================= */

function msUntilNextMidnight() {
  const now = new Date();
  const next = new Date(now);
  next.setHours(24, 0, 0, 0);
  return next.getTime() - now.getTime();
}

/* ========================= Schedule helpers ========================= */

function parseHHMM(value, fallback) {
  if (!value) return fallback;
  const str = String(value).trim().slice(0, 5);
  const [h, m = 0] = str.split(":").map(Number);
  return h * 60 + (m || 0);
}

const getEmptySchedule = (workStart, workEnd, breakStart, breakEnd) => {
  const startMin = parseHHMM(workStart, 8 * 60);
  const endMin = parseHHMM(workEnd, 19 * 60);
  const bStart = breakStart ? parseHHMM(breakStart, null) : null;
  const bEnd = breakEnd ? parseHHMM(breakEnd, null) : null;

  const slots = [];

  // слот = занятие 30 мин, должно закончиться до конца рабочего дня
  for (let t = startMin; t + 30 <= endMin; t += 30) {
    if (bStart !== null && t >= bStart && t < bEnd) continue;

    const h = String(Math.floor(t / 60)).padStart(2, "0");
    const m = String(t % 60).padStart(2, "0");

    slots.push({
      time: `${h}:${m}`,
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
    });
  }

  return slots;
};

const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay() || 7;

  if (day !== 1) {
    d.setDate(d.getDate() - (day - 1));
  }

  d.setHours(0, 0, 0, 0);
  return d;
};

function getFirstFullWeekDays(year, month) {
  const m = new Date(year, month, 1);

  while (m.getDay() !== 1) {
    m.setDate(m.getDate() + 1);
  }

  for (;;) {
    const week = Array.from({ length: 5 }, (_, i) => {
      const d = new Date(m);
      d.setDate(m.getDate() + i);
      return d;
    });

    if (week.every((d) => d.getMonth() === month)) return week;

    m.setDate(m.getDate() + 7);
  }
}

const formatDateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const ALMATY_OFFSET_MS = 5 * 60 * 60 * 1000;

const formatUtcDateKey = (d) =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
    d.getUTCDate()
  ).padStart(2, "0")}`;

const getScheduleDateKey = (value) => {
  if (!value) return "";

  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

  const date = new Date(str);
  if (Number.isNaN(date.getTime())) return str.slice(0, 10);

  return formatUtcDateKey(new Date(date.getTime() + ALMATY_OFFSET_MS));
};

const filterVisitsForWeek = (visits, weekDays) =>
  visits.filter((v) => {
    const dates =
      Array.isArray(v.visitDates) && v.visitDates.length
        ? v.visitDates
        : [v.visitDateFirst].filter(Boolean);

    return dates.some((ds) => {
      const dateKey = getScheduleDateKey(ds);
      return weekDays.some((wd) => formatDateKey(wd) === dateKey);
    });
  });

const updateScheduleForWeek = (visits, spec, weekDays) => {
  const schedule = getEmptySchedule(spec?.workStart, spec?.workEnd, spec?.breakStart, spec?.breakEnd);

  const dateToKey = {};

  weekDays.forEach((d, i) => {
    dateToKey[formatDateKey(d)] = scheduleDayKeys[i];
  });

  (visits || []).forEach((v) => {
    let baseTimes = [];

    if (v.visitTime) {
      baseTimes = [v.visitTime.slice(0, 5)];
    } else {
      const d0 =
        (Array.isArray(v.visitDates) && v.visitDates[0]) || v.visitDateFirst;

      if (d0) {
        const dt = new Date(d0);
        baseTimes = [
          `${String(dt.getHours()).padStart(2, "0")}:${String(
            dt.getMinutes()
          ).padStart(2, "0")}`,
        ];
      }
    }

    const dateArr =
      Array.isArray(v.visitDates) && v.visitDates.length
        ? v.visitDates
        : [v.visitDateFirst].filter(Boolean);

    dateArr.forEach((ds) => {
      const key = dateToKey[getScheduleDateKey(ds)];

      if (!key) return;

      baseTimes.forEach((time) => {
        const slot = schedule.find((s) => s.time === time);

        if (slot) {
          slot[key].push({
            name: "занято",
            kind: "visit",
            status: v.status,
            additional: Boolean(v.additional),
          });
        }
      });
    });
  });

  return schedule;
};

function formatWeekLabel(monday, friday) {
  const d1 = monday.getDate();
  const m1 = monday.getMonth();
  const y1 = monday.getFullYear();
  const d2 = friday.getDate();
  const m2 = friday.getMonth();
  const y2 = friday.getFullYear();

  if (m1 === m2 && y1 === y2) {
    return `${String(d1).padStart(2, "0")}–${String(d2).padStart(
      2,
      "0"
    )} ${russianMonthsGen[m1]} ${y1}`;
  }

  return `${String(d1).padStart(2, "0")} ${
    russianMonthsGen[m1]
  } ${y1} – ${String(d2).padStart(2, "0")} ${
    russianMonthsGen[m2]
  } ${y2}`;
}

/* ======================= QR ======================= */

function QRTodayEmbed() {
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const midnightTimerRef = useRef(null);

  async function load() {
    setLoading(true);

    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort("timeout"), 10000);

    try {
      const resp = await axios.get(API_QR_TODAY, {
        params: { purpose: "attendance" },
        signal: ac.signal,
        validateStatus: () => true,
      });

      if (resp.status < 200 || resp.status >= 300) {
        throw new Error(`HTTP ${resp.status}`);
      }

      setQr(resp.data);
    } catch {
      setQr(null);
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const ms = msUntilNextMidnight();

    midnightTimerRef.current = setTimeout(() => {
      load();
    }, ms + 1000);

    return () => clearTimeout(midnightTimerRef.current);
  }, [qr?.qr_date]);

  if (loading || !qr || qr.offday || !qr.token) return null;

  return <QRCode value={qr.token} size={120} />;
}

/* ======================= Page ======================= */

const TvSchedule = () => {
  useTvNoSleep(true);

  const now = new Date();

  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());

  const [visits, setVisits] = useState([]);

  const [specialists, setSpecialists] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [weekOffset, setWeekOffset] = useState(0);

  const lastInteractionRef = useRef(0);

  const markInteraction = () => {
    lastInteractionRef.current = Date.now();
  };

  const baseMonday = useMemo(() => {
    const today = new Date();

    if (today.getFullYear() === year && today.getMonth() === month) {
      return getMonday(today);
    }

    return getFirstFullWeekDays(year, month)[0];
  }, [year, month]);

  const weekStart = useMemo(() => {
    const d = new Date(baseMonday);
    d.setDate(baseMonday.getDate() + weekOffset * 7);
    return d;
  }, [baseMonday, weekOffset]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + 4);
    return d;
  }, [weekStart]);

  const visibleWeekDays = useMemo(
    () =>
      weekDays
        .map((date, index) => ({ date, dayKey: scheduleDayKeys[index] }))
        .filter(
          ({ date }) =>
            date.getFullYear() === year && date.getMonth() === month
        ),
    [weekDays, year, month]
  );

  const currentSpec = specialists[currentIndex];

  const visitsForWeek = useMemo(
    () =>
      filterVisitsForWeek(
        visits,
        visibleWeekDays.map(({ date }) => date)
      ),
    [visits, visibleWeekDays]
  );

  const scheduleData = useMemo(
    () => updateScheduleForWeek(visitsForWeek, currentSpec || {}, weekDays),
    [visitsForWeek, currentSpec, weekDays]
  );

  useEffect(() => {
    fetchSpecialists()
      .then((data) => {
        const list = Array.isArray(data) ? data : data.employees || [];
        setSpecialists(list.map((s) => ({ ...s, fullName: getFullName(s) })));
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (currentIndex >= specialists.length) {
      setCurrentIndex(0);
    }
  }, [specialists.length, currentIndex]);

  useEffect(() => {
    if (!currentSpec || visibleWeekDays.length === 0) return;

    const specId = getSpecialistId(currentSpec);
    if (!specId) return;

    fetchVisits({
      dateFrom: formatDateKey(visibleWeekDays[0].date),
      dateTo: formatDateKey(visibleWeekDays[visibleWeekDays.length - 1].date),
      specialistId: specId,
      specialistName: currentSpec.fullName,
    })
      .then(setVisits)
      .catch(() => setVisits([]));
  }, [currentSpec, visibleWeekDays]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (specialists.length > 1) {
        const silentMs = Date.now() - lastInteractionRef.current;

        if (silentMs > 20000) {
          setCurrentIndex((i) => (i + 1) % specialists.length);
        }
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [specialists.length]);

  const prevMonth = () => {
    markInteraction();

    setMonth((m) => {
      if (m === 0) {
        setYear((y) => y - 1);
        return 11;
      }

      return m - 1;
    });
  };

  const nextMonth = () => {
    markInteraction();

    setMonth((m) => {
      if (m === 11) {
        setYear((y) => y + 1);
        return 0;
      }

      return m + 1;
    });
  };

  const prevWeek = () => {
    markInteraction();
    setWeekOffset((o) => o - 1);
  };

  const nextWeek = () => {
    markInteraction();
    setWeekOffset((o) => o + 1);
  };

  const resetWeekToCurrent = () => {
    markInteraction();

    const today = new Date();
    const monToday = getMonday(today);

    const diffDays = Math.round(
      (monToday - baseMonday) / (1000 * 60 * 60 * 24)
    );

    const diffWeeks = Math.trunc(diffDays / 7);

    setWeekOffset(diffWeeks);
    setMonth(monToday.getMonth());
    setYear(monToday.getFullYear());
  };

  const prevSpec = () => {
    if (specialists.length === 0) return;

    markInteraction();
    setCurrentIndex((i) => (i - 1 + specialists.length) % specialists.length);
  };

  const nextSpec = () => {
    if (specialists.length === 0) return;

    markInteraction();
    setCurrentIndex((i) => (i + 1) % specialists.length);
  };

  useEffect(() => {
    setWeekOffset(0);
  }, [month, year]);

  const renderTable = (data) => (
    <div className="table-wrapper">
      <table className="specialist-table">
        <thead>
          <tr>
            <th>Время</th>
            {weekDays.map((date) => (
              <th key={date.toISOString()}>{russianWeekdays[date.getDay()]}</th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data.map((slot) => (
            <tr key={slot.time}>
              <td>{slot.time}</td>

              {scheduleDayKeys.map(
                (dayKey) => (
                  <td key={dayKey}>
                    {slot[dayKey].map((it, i) => (
                      <div
                        key={i}
                        style={{
                          fontSize: 28,
                          fontWeight: 600,
                          opacity: 0.9,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {it.name}
                      </div>
                    ))}
                  </td>
                )
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (specialists.length === 0) {
    return (
      <div className="home-container">
        <img className="tv-logo" src={logo} alt="Логотип" />
        <p className="no-specs">Нет специалистов с включённым ТВ-расписанием</p>
      </div>
    );
  }

  const visibleWeekStart = visibleWeekDays[0]?.date || weekStart;
  const visibleWeekEnd =
    visibleWeekDays[visibleWeekDays.length - 1]?.date || weekEnd;
  const weekLabel = formatWeekLabel(visibleWeekStart, visibleWeekEnd);

  return (
    <div className="home-container">
      <img className="tv-logo" src={logo} alt="Логотип" />

      <div className="top-bar">
        <div className="qr-placeholder" aria-hidden="true" />

        <div
          className="month-switcher"
          aria-label="Переключатели периода и специалиста"
        >
          <div
            className="switcher-row"
            role="group"
            aria-label="Переключение месяца"
          >
            <button onClick={prevMonth} aria-label="Предыдущий месяц">
              <FaChevronLeft />
            </button>

            <h1 className="switcher-title">
              Расписание – {russianMonths[month]} {year}
            </h1>

            <button onClick={nextMonth} aria-label="Следующий месяц">
              <FaChevronRight />
            </button>
          </div>

          <div
            className="switcher-row"
            role="group"
            aria-label="Переключение недели"
          >
            <button onClick={prevWeek} aria-label="Предыдущая неделя">
              <FaChevronLeft />
            </button>

            <div className="spec-title">Неделя: {weekLabel}</div>

            <button onClick={nextWeek} aria-label="Следующая неделя">
              <FaChevronRight />
            </button>

            <button
              onClick={resetWeekToCurrent}
              style={{ marginLeft: 12 }}
              title="К текущей неделе"
              aria-label="К текущей неделе"
            />
          </div>

          <div
            className="switcher-row"
            role="group"
            aria-label="Переключение специалиста"
          >
            <button onClick={prevSpec} aria-label="Предыдущий специалист">
              <FaChevronLeft />
            </button>

            <div className="spec-title" style={{ margin: "0 12px" }}>
              Специалист: {specialists[currentIndex]?.fullName}
            </div>

            <button onClick={nextSpec} aria-label="Следующий специалист">
              <FaChevronRight />
            </button>
          </div>
        </div>

        <div className="qr-top-container" title="QR на сегодня">
          <QRTodayEmbed />
        </div>
      </div>

      <h2 className="visually-hidden">
        Специалист: {specialists[currentIndex]?.fullName}
      </h2>

      <div className="table-container">{renderTable(scheduleData)}</div>
    </div>
  );
};

export default TvSchedule;